import { useMemo, useState } from 'react';
import { User2 } from 'lucide-react';
import { toast } from 'sonner';

import type { BusinessProfileBootstrapInput } from '@/domain/entities/user';
import type { SupportedCountryCodes } from '@/lib';
import { formatNanpPhone, toE164FromNanp } from '@/lib/validation/phone';
import { bootstrapUserProfile, selectProfileLoading, updateUserProfile } from '@/features/users';
import { selectCurrentUserProfile } from '@/store/selectors/authorizationSelectors';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  createProfileSchema,
  type CreateProfileFormValues,
  type ProfileFieldErrors,
  toProfileFieldErrors,
} from './profileForm';

type BusinessProfileEditorProps = {
  title?: string;
  description?: string;
  submitCreateLabel?: string;
  submitUpdateLabel?: string;
  className?: string;
  onSuccess?: () => void;
};

export function BusinessProfileEditor({
  title = 'Complete your profile',
  description = 'We need a few details to set up your business account.',
  submitCreateLabel = 'Create Profile',
  submitUpdateLabel = 'Update Profile',
  className,
  onSuccess,
}: BusinessProfileEditorProps) {
  const dispatch = useAppDispatch();
  const email = useAppSelector((s) => s.auth.email);
  const isLoading = useAppSelector(selectProfileLoading);
  const existingProfile = useAppSelector(selectCurrentUserProfile);
  const isEditing = !!existingProfile;

  const [values, setValues] = useState<CreateProfileFormValues>(() => ({
    firstName: existingProfile?.firstName ?? '',
    lastName: existingProfile?.lastName ?? '',
    phone: existingProfile?.phone ? formatNanpPhone(existingProfile.phone) : '',
    country: (existingProfile?.address?.country ?? 'CA') as SupportedCountryCodes,
    addressLine1: existingProfile?.address?.line1 ?? '',
    addressLine2: existingProfile?.address?.line2 ?? '',
    city: existingProfile?.address?.city ?? '',
    region: existingProfile?.address?.region ?? '',
    postalCode: existingProfile?.address?.postalCode ?? '',
  }));
  const [errors, setErrors] = useState<ProfileFieldErrors>({});

  const isValid = useMemo(() => createProfileSchema.safeParse(values).success, [values]);

  const setField = <K extends keyof CreateProfileFormValues>(key: K, val: CreateProfileFormValues[K]) => {
    setValues((s) => ({ ...s, [key]: val }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validateField = (key: keyof CreateProfileFormValues) => {
    const result = createProfileSchema.safeParse(values);
    if (result.success) {
      if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
      return;
    }

    const next = toProfileFieldErrors(result.error);
    if (next[key]) setErrors((e) => ({ ...e, [key]: next[key] }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast.error('No email found in session. Please log out and log back in.');
      return;
    }

    const parsed = createProfileSchema.safeParse(values);
    if (!parsed.success) {
      setErrors(toProfileFieldErrors(parsed.error));
      toast.error('Please fix the highlighted fields.');
      return;
    }

    const profileInput: BusinessProfileBootstrapInput = {
      firstName: parsed.data.firstName,
      lastName: parsed.data.lastName,
      phone: toE164FromNanp(parsed.data.phone),
      address: {
        line1: parsed.data.addressLine1,
        line2: parsed.data.addressLine2?.trim() ? parsed.data.addressLine2.trim() : undefined,
        city: parsed.data.city,
        region: parsed.data.region,
        postalCode: parsed.data.postalCode,
        country: parsed.data.country,
      },
    };

    const promise = existingProfile
      ? dispatch(updateUserProfile({ profile: { ...existingProfile, ...profileInput } }))
          .unwrap()
          .then((response) => response.data)
      : dispatch(bootstrapUserProfile(profileInput)).unwrap();

    toast.promise(promise, {
      loading: isEditing ? 'Updating your profile…' : 'Saving your profile…',
      success: () => {
        onSuccess?.();
        return isEditing ? 'Profile updated successfully!' : 'Profile created successfully!';
      },
      error: (err) => err?.message || (isEditing ? 'Failed to update profile' : 'Failed to create profile'),
    });
  };

  const FieldError = ({ name }: { name: keyof CreateProfileFormValues }) =>
    errors[name] ? <p className="text-destructive text-xs">{errors[name]}</p> : null;

  return (
    <Card className={className ?? 'w-full max-w-xl'}>
      <CardHeader className="text-center">
        <div className="bg-primary/10 mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full">
          <User2 className="text-primary h-8 w-8" />
        </div>
        <CardTitle className="text-2xl">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={values.firstName}
                onChange={(e) => setField('firstName', e.target.value)}
                onBlur={() => validateField('firstName')}
                autoComplete="given-name"
                required
              />
              <FieldError name="firstName" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={values.lastName}
                onChange={(e) => setField('lastName', e.target.value)}
                onBlur={() => validateField('lastName')}
                autoComplete="family-name"
                required
              />
              <FieldError name="lastName" />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <div className="relative">
                <span className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2 text-sm">+1</span>
                <Input
                  id="phone"
                  className="pl-10"
                  placeholder="(000) 000-0000"
                  value={values.phone}
                  onChange={(e) => setField('phone', formatNanpPhone(e.target.value))}
                  onBlur={() => validateField('phone')}
                  inputMode="tel"
                  autoComplete="tel"
                  required
                />
              </div>
              <FieldError name="phone" />
            </div>

            <div className="space-y-2">
              <Label>Country</Label>
              <Select value={values.country} onValueChange={(val) => setField('country', val as SupportedCountryCodes)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CA">🇨🇦 Canada</SelectItem>
                  <SelectItem value="US">🇺🇸 United States</SelectItem>
                </SelectContent>
              </Select>
              {errors.country ? <p className="text-destructive text-xs">{errors.country}</p> : null}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="addressLine1">Address Line 1</Label>
            <Input
              id="addressLine1"
              placeholder="123 Main St"
              value={values.addressLine1}
              onChange={(e) => setField('addressLine1', e.target.value)}
              onBlur={() => validateField('addressLine1')}
              autoComplete="address-line1"
              required
            />
            <FieldError name="addressLine1" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="addressLine2">Address Line 2 (Optional)</Label>
            <Input
              id="addressLine2"
              placeholder="Suite 400"
              value={values.addressLine2 ?? ''}
              onChange={(e) => setField('addressLine2', e.target.value)}
              autoComplete="address-line2"
            />
            <FieldError name="addressLine2" />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={values.city}
                onChange={(e) => setField('city', e.target.value)}
                onBlur={() => validateField('city')}
                autoComplete="address-level2"
                required
              />
              <FieldError name="city" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="region">State / Province</Label>
              <Input
                id="region"
                value={values.region}
                onChange={(e) => setField('region', e.target.value)}
                onBlur={() => validateField('region')}
                autoComplete="address-level1"
                required
              />
              <FieldError name="region" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="postalCode">Postal / ZIP</Label>
              <Input
                id="postalCode"
                value={values.postalCode}
                onChange={(e) => setField('postalCode', e.target.value)}
                onBlur={() => validateField('postalCode')}
                autoComplete="postal-code"
                required
              />
              <FieldError name="postalCode" />
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={isLoading || !isValid}>
            {isLoading ? 'Saving…' : isEditing ? submitUpdateLabel : submitCreateLabel}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
